import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Home,
  GraduationCap,
  Dumbbell,
  Brain,
  ExternalLink,
  Video,
  FileText,
  Users,
  BookOpen,
  Briefcase,
  Globe,
  Heart,
  CalendarDays,
  ScrollText,
  Trophy,
  Search,
  IdCard,
  Sparkles,
} from "lucide-react";

/**
 * Player Hub Sidebar — left-hand navigation for the Player Dashboard.
 * Hierarchy: NSB Connect (Locker Room) → NSB Academy → NSB University
 *
 * Items have one of three behaviours:
 *  - tabKey:    switches the dashboard tab in-place
 *  - internal:  React Router route to an internal sub-page
 *  - sso:       Single Sign-On launch via /sso/launch/:partner
 */
export function PlayerHubSidebar({ activeTab, onSelectTab }) {
  const [openSections, setOpenSections] = useState({
    connect: true,
    academy: false,
    university: false,
  });
  const [openSub, setOpenSub] = useState({ liveSessions: false, mentalPerf: false });

  const toggle = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));
  const toggleSub = (key) => setOpenSub((s) => ({ ...s, [key]: !s[key] }));

  const Section = ({ id, icon: Icon, label, accent, children, badge }) => {
    const open = openSections[id];
    return (
      <div className="mb-1">
        <button
          onClick={() => toggle(id)}
          className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-all group ${
            open ? "bg-white shadow-sm" : "hover:bg-white/60"
          }`}
          data-testid={`hub-section-${id}`}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}>
            <Icon size={16} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-gray-900 tracking-tight">{label}</div>
            {badge && <div className="text-[10px] text-gray-500 uppercase tracking-wider">{badge}</div>}
          </div>
          {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
        </button>
        {open && <div className="pl-2 pt-1 pb-2 space-y-0.5">{children}</div>}
      </div>
    );
  };

  const TabItem = ({ tabKey, label, icon: Icon, description }) => {
    const isActive = activeTab === tabKey;
    return (
      <button
        onClick={() => onSelectTab && onSelectTab(tabKey)}
        className={`w-full flex items-start gap-2 pl-3 pr-2 py-2 rounded-md text-left transition group ${
          isActive ? "bg-blue-50 border-l-2 border-blue-900" : "hover:bg-gray-50 border-l-2 border-transparent"
        }`}
        data-testid={`hub-tab-${tabKey}`}
      >
        {Icon && <Icon size={14} className={`mt-0.5 flex-shrink-0 ${isActive ? "text-blue-900" : "text-gray-400"}`} />}
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-semibold ${isActive ? "text-blue-900" : "text-gray-700 group-hover:text-gray-900"}`}>{label}</div>
          {description && <div className="text-[10px] text-gray-500 leading-tight mt-0.5">{description}</div>}
        </div>
      </button>
    );
  };

  const LinkItem = ({ to, label, icon: Icon, logo, description, kind = "internal" }) => (
    <Link
      to={to}
      target={kind === "sso" ? "_blank" : undefined}
      rel={kind === "sso" ? "noopener noreferrer" : undefined}
      className="w-full flex items-start gap-2 pl-3 pr-2 py-2 rounded-md text-left transition hover:bg-gray-50 border-l-2 border-transparent group"
      data-testid={`hub-link-${to.replace(/[^a-z0-9]+/gi, "-")}`}
    >
      {logo ? (
        <div className="w-5 h-5 rounded bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 mt-0.5">
          <img src={logo} alt="" className="w-full h-full object-contain" />
        </div>
      ) : (
        Icon && <Icon size={14} className="mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-gray-700" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-gray-700 group-hover:text-gray-900 flex items-center gap-1">
          {label}
          {kind === "sso" && <ExternalLink size={9} className="text-gray-400" />}
        </div>
        {description && <div className="text-[10px] text-gray-500 leading-tight mt-0.5">{description}</div>}
      </div>
    </Link>
  );

  const SubGroup = ({ id, label, children }) => {
    const open = openSub[id];
    return (
      <div>
        <button
          onClick={() => toggleSub(id)}
          className="w-full flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-md hover:bg-gray-50 text-left"
          data-testid={`hub-subgroup-${id}`}
        >
          {open ? <ChevronDown size={11} className="text-gray-400" /> : <ChevronRight size={11} className="text-gray-400" />}
          <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{label}</span>
        </button>
        {open && <div className="pl-3 space-y-0.5">{children}</div>}
      </div>
    );
  };

  return (
    <aside className="hidden lg:block w-72 flex-shrink-0 bg-gray-50 border-r border-gray-200 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto p-3" data-testid="player-hub-sidebar">
      <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold px-2 mb-2">Player Hub</div>

      {/* NSB Connect (Locker Room — the dashboard itself) */}
      <Section id="connect" icon={Home} label="NSB Connect" badge="Locker Room" accent="bg-gradient-to-br from-red-600 to-red-700">
        <TabItem tabKey="overview" label="Locker Room — Profile" icon={IdCard} description="Player Report Page" />
        <TabItem tabKey="performance" label="Player Report Page" icon={Trophy} description="Testing results & metrics" />
        <TabItem tabKey="schools" label="Coach Search Visibility" icon={Search} description="Visible to 6,000+ coaches" />
        <TabItem tabKey="overview" label="Extended Profile Resume" icon={ScrollText} description="Full athletic resume" />
        <LinkItem to="/coach-portal" label="College Coach Portal" icon={Users} kind="internal" description="Direct coach communication" />
        <LinkItem to="/insights" label="College Baseball Insights" icon={Sparkles} kind="internal" description="Data-driven trends" />
        <LinkItem to="/recruiting-guide" label="85-page Recruiting Guide" icon={BookOpen} kind="internal" description="Complete playbook" />
        <LinkItem to="/ncaa-rules" label="NCAA Recruiting Rules" icon={ScrollText} kind="internal" description="Compliance guidelines" />
        <LinkItem to="/premium-videos" label="Premium Development Videos" icon={Video} kind="internal" description="Player information library" />
        <LinkItem to="/weekly-zoom" label="Weekly Recruiting Zoom Calls" icon={CalendarDays} kind="internal" description="Discounted group support" />
      </Section>

      {/* NSB Academy — Physical Development */}
      <Section id="academy" icon={Dumbbell} label="NSB Academy" badge="Player Development" accent="bg-gradient-to-br from-emerald-600 to-emerald-700">
        <LinkItem to="/combines" label="Combine & Camp System" icon={Trophy} kind="internal" description="FlightScope-verified events" />
        <SubGroup id="liveSessions" label="Live Sessions">
          <LinkItem to="/live-zoom" label="Live Zoom Group Training" icon={Video} kind="internal" description="Group instruction" />
          <LinkItem to="/sso/launch/demotu" logo="/partner-logos/demotu.png" label="Demotu" kind="sso" description="FMS + Speed/Agility/Strength" />
          <LinkItem to="/sso/launch/gamesense" logo="/partner-logos/gamesense.png" label="GameSense" kind="sso" description="Pitch recognition" />
        </SubGroup>
      </Section>

      {/* NSB University — Mental, IQ, Brand */}
      <Section id="university" icon={GraduationCap} label="NSB University" badge="Mental, IQ & Brand" accent="bg-gradient-to-br from-amber-600 to-amber-700">
        <LinkItem to="/sso/launch/sage-scholars" logo="/partner-logos/sage-scholars.jpg" label="SAGE Scholars Tuition Rewards" kind="sso" description="400+ partner colleges" />
        <LinkItem to="/sso/launch/6tool" logo="/partner-logos/6tool.png" label="Baseball IQ — 6 Tool" kind="sso" description="Cognitive baseball training" />
        <SubGroup id="mentalPerf" label="Mental Performance">
          <LinkItem to="/sso/launch/quality-at-bats" logo="/partner-logos/quality-at-bats.png" label="Game Mental IQ — Steve Springer" kind="sso" description="Quality At-Bats course" />
          <LinkItem to="/youth-potential" label="Youth Potential / Neuro-Change" icon={Brain} kind="internal" description="Mindset training" />
          <LinkItem to="/mental-health" label="Mental Health Support" icon={Heart} kind="internal" description="Athlete wellness" />
        </SubGroup>
        <LinkItem to="/market-u" label="Market U — Brand Development" icon={Briefcase} kind="internal" description="Personal brand & NIL" />
        <LinkItem to="/sso/launch/college-ed-insights" logo="/partner-logos/college-ed-insights.svg" label="CollegeEdInsights" kind="sso" description="College recruitment data & analytics" />
        <LinkItem to="/nsb-online" label="NSB Online" icon={Globe} kind="internal" description="Digital training library" />
      </Section>

      <div className="px-3 py-3 mt-2 border-t border-gray-200 text-[10px] text-gray-400 leading-relaxed">
        More partners coming soon. NSB Academy & University expand as your development tier grows.
      </div>
    </aside>
  );
}
